// The `netwatch-chart` block schema, normalised into something a renderer can
// draw without further thought. Pure: no React, no chart.js, no DOM — so it is
// testable under the node-only vitest runner the dashboard already has.
//
// Two data modes, chosen by what the block carries:
//   categorical — top-level `labels[]` + per-series `data[]` (bar, pie, doughnut)
//   continuous  — per-series `points[]`, `{t,y}` (time), `{x,y}` numeric, or
//                 `{x:"label",y}` (line, scatter, and the legacy shape)
//
// Everything here is total: a malformed or impossible block returns
// `{ok:false, reason}` and the renderer shows a fallback. Nothing throws, and
// nothing renders a chart that says something the data doesn't.

export type NwChartType = 'line' | 'bar' | 'pie' | 'doughnut' | 'scatter'

const TYPES: NwChartType[] = ['line', 'bar', 'pie', 'doughnut', 'scatter']
const ARC_TYPES: NwChartType[] = ['pie', 'doughnut']

/** Caps. Beyond these a chart stops being readable, so we say so rather than draw it. */
export const MAX_SERIES = 6          // the validated categorical palette is six deep
export const MAX_POINTS = 400        // per series
export const MAX_CATEGORIES = 60     // bar/line category axis
export const MAX_SLICES = 8          // pie/doughnut, extras merge into "Other"
export const MAX_BAR_CATEGORIES = 40 // above this a bar chart is a smear → draw a line
export const MAX_UNITS = 2           // left axis + right axis

/** Positional series colours — the ramp validated in docs/charts.md (six
 *  checks, both surfaces). Assigned in fixed order and NEVER cycled: two
 *  identically coloured series is a lie. The two hues a viewer could mistake
 *  for semaphore status sit in slots 5-6, so a 2-4 series chart never shows
 *  one. Fallbacks are the dark steps, matching the dark-default convention. */
export const PALETTE_VARS = [
  '--nw-chart-1', '--nw-chart-2', '--nw-chart-3',
  '--nw-chart-4', '--nw-chart-5', '--nw-chart-6',
] as const
export const PALETTE_FALLBACKS = [
  '#3987e5', '#c98500', '#d55181', '#9085e9', '#d95926', '#199e70',
] as const

/** The chat cascade's single accent, kept for blocks written before the
 *  multi-series schema so their colour does not shift under existing threads. */
export const LEGACY_COLOR_VAR = '--nw-accent'
export const LEGACY_COLOR_FALLBACK = '#5b93e8'

/** Reference series (`muted: true`): the host's tertiary ink, so it reads as
 *  context next to a palette-coloured subject in both themes. */
export const MUTED_COLOR_VAR = '--nw-text-tertiary'
export const MUTED_COLOR_FALLBACK = '#8b95a5'

export interface ChartPoint {
  t?: number
  x?: number | string
  y: number
}

export interface ChartBlockSeries {
  label?: string
  points?: ChartPoint[]
  data?: (number | null)[]
  unit?: string
  dashed?: boolean
  fill?: boolean
  /** A reference series (the findings block's lead metric): drawn thin and
   *  grey so the coloured series stays the subject. */
  muted?: boolean
}

/** The JSON inside a ```netwatch-chart fence, as written. */
export interface ChartBlock {
  type?: string
  title?: string
  unit?: string
  threshold?: number
  labels?: unknown[]
  series?: ChartBlockSeries[]
  points?: ChartPoint[]
  stacked?: boolean
  horizontal?: boolean
  xLabel?: string
  yLabel?: string
  yMax?: number
  ref?: string
  [key: string]: unknown
}

export interface SpecSeries {
  label: string
  /** Continuous mode: plotted points, x already numeric. */
  points: { x: number; y: number }[]
  /** Categorical mode: one value per label, null = gap. */
  data: (number | null)[]
  unit: string
  /** Which value axis this series belongs to ('y1' = the right-hand one). */
  axis: 'y' | 'y1'
  colorVar: string
  fallback: string
  dashed: boolean
  fillAlpha: number
  muted: boolean
}

export interface ChartSpec {
  type: NwChartType
  title: string
  unit: string
  threshold: number | null
  categorical: boolean
  /** Category labels (categorical mode) or x tick labels (index mode). */
  labels: string[]
  series: SpecSeries[]
  /** Distinct units in series order; index 0 → left axis, 1 → right axis. */
  units: string[]
  stacked: boolean
  horizontal: boolean
  xLabel: string
  yLabel: string
  yMax: number | null
  /** Head-room ceiling used when `yMax` is unset, so a line doesn't touch the
   *  top of its own frame. Percent charts pin yMax instead. */
  suggestedMax: number | null
  /** The same for the right-hand axis (two-unit charts, or a muted reference
   *  series): its own scale, or the reference flattens the subject. */
  suggestedMaxRight: number | null
  /** x values are epoch seconds → format ticks as clock times or dates. */
  timed: boolean
  /** Seconds covered by the x axis; picks the time format (see xTickLabel). */
  xSpan: number
  /** IANA zone the axis and tooltips render in — the zone the server composed
   *  the surrounding answer in. A chart under a findings card must not read a
   *  different clock than the strip above it. Null falls back to the viewer's.
   *  See docs/timezones.md. */
  tz?: string | null
  /** Caller-supplied tick formatter, for surfaces with their own x vocabulary
   *  (Outlook's day counts). Overrides the built-in date/label formatting. */
  xTickFormat?: (x: number) => string
  /** Explicit x positions to label (continuous mode). */
  xTickValues: number[]
  /** Visible x window when the reader has zoomed in. The series keep every
   *  point — only the axis bounds move, so panning back reveals real data
   *  rather than something re-fetched or re-interpolated. */
  xMin?: number
  xMax?: number
  /** Direct-label the last point — the legacy single-series treatment. */
  lastValueLabel: boolean
  legacy: boolean
  height: number
  /** Anything we dropped, so the UI can say so instead of quietly lying. */
  notes: string[]
}

export type NormalizeResult =
  | { ok: true; spec: ChartSpec }
  | { ok: false; reason: string }

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

/** A chart payload resolved from a `ref` — data only; presentation stays with
 *  the block the model wrote. */
export type ChartPayload = Pick<ChartBlock, 'labels' | 'series' | 'points' | 'unit' | 'threshold'>

export function mergeRefPayload(block: ChartBlock, payload?: ChartPayload | null): ChartBlock {
  if (hasInlineData(block) || !payload) return block
  return { ...payload, ...block, ref: block.ref }
}

export function hasInlineData(block: ChartBlock | null | undefined): boolean {
  if (!block) return false
  return (
    (Array.isArray(block.points) && block.points.length > 0) ||
    (Array.isArray(block.series) && block.series.length > 0) ||
    (Array.isArray(block.labels) && block.labels.length > 0)
  )
}

function chartType(raw: unknown): NwChartType {
  const t = String(raw ?? 'line').toLowerCase()
  if (t === 'area') return 'line' // alias: a filled line
  return (TYPES as string[]).includes(t) ? (t as NwChartType) : 'line'
}

function stride<T>(items: T[], max: number): T[] {
  if (items.length <= max) return items
  const step = (items.length - 1) / (max - 1)
  const out: T[] = []
  for (let i = 0; i < max; i++) out.push(items[Math.round(i * step)])
  out[out.length - 1] = items[items.length - 1]
  return out
}

/** Truncate a category label for a narrow axis; the tooltip keeps the full one. */
export function clipLabel(label: string, max = 16): string {
  return label.length <= max ? label : `${label.slice(0, max - 1)}…`
}

export function paletteSlot(index: number): { colorVar: string; fallback: string } {
  const i = Math.min(index, PALETTE_VARS.length - 1)
  return { colorVar: PALETTE_VARS[i], fallback: PALETTE_FALLBACKS[i] }
}

/** Height per type. Set in JS because the widget panel is user-resizable, so a
 *  media query cannot see how wide the chart actually is. */
export function chartHeight(spec: Pick<ChartSpec, 'type' | 'horizontal' | 'labels' | 'series'>): number {
  const legend = spec.series.length > 1 ? 16 : 0
  switch (spec.type) {
    case 'pie':
    case 'doughnut':
      return 240
    case 'bar':
      if (spec.horizontal) {
        return Math.max(120, Math.min(44 + 26 * spec.labels.length, 420))
      }
      return 200 + legend
    default:
      return 180 + legend
  }
}

function normaliseSeries(block: ChartBlock): { list: ChartBlockSeries[]; legacy: boolean } {
  if (Array.isArray(block.series) && block.series.length) {
    return { list: block.series.filter((s) => s && typeof s === 'object'), legacy: false }
  }
  if (Array.isArray(block.points) && block.points.length) {
    // Legacy single-series shorthand.
    return {
      list: [{ label: block.title || 'value', points: block.points, fill: true }],
      legacy: true,
    }
  }
  return { list: [], legacy: false }
}

function readPoints(raw: ChartPoint[]): { points: { x: number; y: number }[]; timed: boolean; labels: string[] } {
  const clean = raw.filter((p) => p && num(p.y) !== null)
  const timed = clean.length > 0 && clean.every((p) => num(p.t) !== null)
  const numericX = clean.length > 0 && clean.every((p) => num(p.x) !== null)
  if (timed) {
    return { points: clean.map((p) => ({ x: p.t as number, y: p.y })), timed: true, labels: [] }
  }
  if (numericX) {
    return { points: clean.map((p) => ({ x: p.x as number, y: p.y })), timed: false, labels: [] }
  }
  return {
    points: clean.map((p, i) => ({ x: i, y: p.y })),
    timed: false,
    labels: clean.map((p) => String(p.x ?? '')),
  }
}

export function normalizeChartBlock(
  block: ChartBlock | null | undefined,
  payload?: ChartPayload | null,
): NormalizeResult {
  if (!block || typeof block !== 'object') return { ok: false, reason: 'not a chart block' }
  const merged = mergeRefPayload(block, payload)
  if (!hasInlineData(merged)) {
    return { ok: false, reason: merged.ref ? 'chart data not loaded' : 'no data in block' }
  }

  let type = chartType(merged.type)
  const notes: string[] = []
  const { list, legacy } = normaliseSeries(merged)
  if (!list.length) return { ok: false, reason: 'no series' }

  let kept = list
  if (kept.length > MAX_SERIES && !ARC_TYPES.includes(type)) {
    notes.push(`showing ${MAX_SERIES} of ${kept.length} series`)
    kept = kept.slice(0, MAX_SERIES)
  }
  if (ARC_TYPES.includes(type) && kept.length > 1) {
    notes.push('a pie shows one series — drawing the first')
    kept = kept.slice(0, 1)
  }

  const rawLabels = Array.isArray(merged.labels) ? merged.labels.map((l) => String(l)) : []
  const categorical = rawLabels.length > 0
  const topUnit = typeof merged.unit === 'string' ? merged.unit : ''

  let labels = rawLabels
  let timed = false
  let series: SpecSeries[] = []

  if (categorical) {
    labels = stride(labels, MAX_CATEGORIES)
    const width = labels.length
    series = kept.map((s, i) => ({
      label: String(s.label ?? `Series ${i + 1}`),
      points: [],
      data: Array.from({ length: width }, (_, c) => num((s.data ?? [])[c])),
      unit: typeof s.unit === 'string' ? s.unit : topUnit,
      axis: 'y' as const,
      ...paletteSlot(i),
      dashed: false,
      fillAlpha: 0,
      muted: false,
    }))
  } else {
    if (ARC_TYPES.includes(type)) {
      // Slices of a whole need named parts; a time series has none. Refusing
      // beats drawing 60 timestamps as a pie.
      return { ok: false, reason: 'a pie needs labelled categories, not a time series' }
    }
    const read = kept.map((s) => readPoints(Array.isArray(s.points) ? s.points : []))
    if (read.every((r) => r.points.length === 0)) return { ok: false, reason: 'no plottable points' }
    timed = read.some((r) => r.timed)
    labels = read.find((r) => r.labels.length)?.labels ?? []
    series = kept.map((s, i) => {
      const points = stride(read[i].points, MAX_POINTS)
      if (read[i].points.length > MAX_POINTS) notes.push(`${s.label ?? 'series'}: thinned to ${MAX_POINTS} points`)
      const muted = Boolean(s.muted)
      const color = muted
        ? { colorVar: MUTED_COLOR_VAR, fallback: MUTED_COLOR_FALLBACK }
        : legacy ? { colorVar: LEGACY_COLOR_VAR, fallback: LEGACY_COLOR_FALLBACK } : paletteSlot(i)
      return {
        label: String(s.label ?? `Series ${i + 1}`),
        points,
        data: [],
        unit: typeof s.unit === 'string' ? s.unit : topUnit,
        axis: 'y' as const,
        ...color,
        dashed: Boolean(s.dashed),
        fillAlpha: s.fill || legacy ? 0.12 : 0,
        muted,
      }
    })
  }

  // A bar chart of hundreds of samples is a smear — draw the line it really is.
  if (type === 'bar' && !categorical) {
    const widest = Math.max(...series.map((s) => s.points.length))
    if (widest > MAX_BAR_CATEGORIES) {
      type = 'line'
      notes.push('too many points for bars — drawn as a line')
    }
  }

  let units = Array.from(new Set(series.map((s) => s.unit)))
  if (units.length > MAX_UNITS) {
    return { ok: false, reason: `${units.length} different units can't share one chart` }
  }
  // Two units → two axes, assigned in series order. Both still start at 0.
  series = series.map((s) => ({ ...s, axis: units.indexOf(s.unit) === 1 ? 'y1' : 'y' }))
  // A muted reference series reads on its own (right) axis even when it
  // shares the subject's unit: it is context at a different scale, and one
  // axis flattens whichever of the two is smaller (a 25ms disk latency next
  // to a 400ms RTT drew as a straight line).
  if (units.length === 1 && series.some((s) => s.muted) && series.some((s) => !s.muted)) {
    series = series.map((s) => (s.muted ? { ...s, axis: 'y1' as const } : s))
    units = [units[0], units[0]]
  }

  const effectiveUnit = new Set(units).size === 1 ? units[0] : ''
  const threshold = num(merged.threshold)
  // SORTED: with two series this is a concatenation, so the "middle" element
  // is the seam between them — a 24h two-metric chart was labelling its middle
  // tick with the first series' LAST timestamp (all three ticks read 13:13).
  const xs = series.flatMap((s) => s.points.map((p) => p.x)).sort((a, b) => a - b)
  const mid = xs.length > 2 ? [xs[Math.floor((xs.length - 1) / 2)]] : []
  const yMax = num(merged.yMax) ?? (effectiveUnit === '%' ? 100 : null)
  const valuesOn = (axis: 'y' | 'y1') => series
    .filter((s) => s.axis === axis)
    .flatMap((s) => (s.points.length ? s.points.map((p) => p.y) : s.data.filter((v): v is number => v !== null)))
  const values = valuesOn('y')
  const rightValues = valuesOn('y1')

  const spec: ChartSpec = {
    type,
    title: typeof merged.title === 'string' ? merged.title : '',
    unit: effectiveUnit,
    threshold: ARC_TYPES.includes(type) ? null : threshold,
    categorical,
    labels,
    series,
    units,
    stacked: type === 'bar' && Boolean(merged.stacked),
    horizontal: type === 'bar' && Boolean(merged.horizontal),
    xLabel: typeof merged.xLabel === 'string' ? merged.xLabel : '',
    yLabel: typeof merged.yLabel === 'string' ? merged.yLabel : '',
    yMax,
    suggestedMax: yMax == null
      ? Math.ceil(Math.max(...values, threshold ?? -Infinity, 1) * 1.1)
      : null,
    suggestedMaxRight: rightValues.length ? Math.ceil(Math.max(...rightValues, 1) * 1.1) : null,
    timed,
    xSpan: xs.length ? Math.max(...xs) - Math.min(...xs) : 0,
    tz: typeof merged.tz === 'string' && merged.tz ? merged.tz : null,
    xTickValues: categorical ? [] : xs.length ? [xs[0], ...mid, xs[xs.length - 1]] : [],
    lastValueLabel: legacy,
    legacy,
    height: 0,
    notes,
  }
  spec.height = chartHeight(spec)

  if (ARC_TYPES.includes(type)) {
    const slices = spec.series[0]?.data ?? []
    if (slices.length > MAX_SLICES) {
      const head = slices.slice(0, MAX_SLICES - 1)
      const rest = slices.slice(MAX_SLICES - 1).reduce<number>((sum, v) => sum + (v ?? 0), 0)
      spec.series[0] = { ...spec.series[0], data: [...head, rest] }
      spec.labels = [...spec.labels.slice(0, MAX_SLICES - 1), 'Other']
      spec.notes.push(`${slices.length - MAX_SLICES + 1} smaller slices merged into "Other"`)
    }
  }

  return { ok: true, spec }
}

/** The smallest window a zoom may leave, so a stray drag can't land on a
 *  single pixel of time: 1% of the full span, or two samples, whichever is
 *  larger. */
export function minZoomSpan(spec: ChartSpec): number {
  const xs = spec.series.flatMap((s) => s.points.map((p) => p.x)).sort((a, b) => a - b)
  if (xs.length < 2) return 0
  const gaps = xs.slice(1).map((x, i) => x - xs[i]).filter((d) => d > 0)
  const tightest = gaps.length ? Math.min(...gaps) : 0
  return Math.max(spec.xSpan / 100, tightest * 2)
}

/** The same chart, showing only [from, to] of its x axis.
 *
 *  Nothing is dropped or recomputed — the axis bounds move and the ticks are
 *  re-placed inside the new window, so what you read after zooming is the same
 *  data you were already looking at.
 */
export function zoomedSpec(spec: ChartSpec, from: number, to: number, ticks = 5): ChartSpec {
  if (spec.categorical || spec.series.every((s) => !s.points.length)) return spec
  const xs = spec.series.flatMap((s) => s.points.map((p) => p.x))
  const floor = Math.min(...xs)
  const ceiling = Math.max(...xs)
  let lo = Math.max(floor, Math.min(from, to))
  let hi = Math.min(ceiling, Math.max(from, to))
  const smallest = minZoomSpan(spec)
  if (hi - lo < smallest) {
    // Grow the selection around its own centre rather than refusing it.
    const centre = (lo + hi) / 2
    lo = Math.max(floor, centre - smallest / 2)
    hi = Math.min(ceiling, centre + smallest / 2)
  }
  if (hi <= lo) return spec

  const step = (hi - lo) / (ticks - 1)
  const xTickValues = Array.from({ length: ticks }, (_, i) => Math.round(lo + i * step))
  return { ...spec, xMin: lo, xMax: hi, xSpan: hi - lo, xTickValues }
}

/** The same chart, sized for a full-screen dialog.
 *
 *  A wider canvas can carry more x labels honestly — three ticks on a 900px
 *  chart wastes the room the reader just asked for — so the tick set grows
 *  with the space, evenly spaced over the real data. */
export function expandedSpec(spec: ChartSpec, height: number, ticks = 6): ChartSpec {
  if (spec.categorical || !spec.series.length) return { ...spec, height }
  const xs = spec.series.flatMap((s) => s.points.map((p) => p.x)).sort((a, b) => a - b)
  if (xs.length < 2) return { ...spec, height }
  const wanted = Math.min(ticks, xs.length)
  const step = (xs.length - 1) / (wanted - 1)
  const xTickValues = Array.from(new Set(
    Array.from({ length: wanted }, (_, i) => xs[Math.round(i * step)]),
  ))
  return { ...spec, height, xTickValues }
}

const DAY = 86400

/** Add the chart's zone to a format option bag, when it has one.
 *
 *  An axis drawn from epochs picks the viewer's browser clock unless it is
 *  told otherwise, which is how a chart came to sit under a strip reading a
 *  different hour for the same instant. `undefined` locale is deliberate —
 *  the reader's number and month names stay theirs; only the ZONE is pinned.
 *  An unusable zone name falls back rather than throwing.
 */
function zoned(tz?: string | null): (o: Intl.DateTimeFormatOptions) => Intl.DateTimeFormatOptions {
  if (!tz) return (o) => o
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
  } catch {
    return (o) => o
  }
  return (o) => ({ ...o, timeZone: tz })
}

/** Format one x value for an axis tick.
 *
 *  The format follows the WINDOW, not the value: six one-hour ticks all
 *  reading "Aug 12" tell the reader nothing, and neither does "14:05" on a
 *  chart spanning a year. Up to 36h the axis is a clock (a "last 24h" window
 *  crosses at most one midnight, and the tooltip still names the day); beyond
 *  that it is a calendar; past a year it drops the day.
 */
export function xTickLabel(spec: ChartSpec, value: number): string {
  if (spec.xTickFormat) return spec.xTickFormat(value)
  if (spec.categorical) return spec.labels[Math.round(value)] ?? ''
  if (spec.timed) {
    const at = new Date(value * 1000)
    const z = zoned(spec.tz)
    if (spec.xSpan <= 36 * 3600) {
      return at.toLocaleTimeString(undefined, z({ hour: '2-digit', minute: '2-digit' }))
    }
    if (spec.xSpan <= 330 * DAY) {
      return at.toLocaleDateString(undefined, z({ month: 'short', day: 'numeric' }))
    }
    return at.toLocaleDateString(undefined, z({ month: 'short', year: 'numeric' }))
  }
  if (spec.labels.length) return spec.labels[Math.round(value)] ?? ''
  return String(value)
}

/** Format one x value for a tooltip title.
 *
 *  A tooltip has room the axis doesn't, and it answers "when exactly?" — so it
 *  carries both halves whenever the axis had to drop one. Same rule for a
 *  6-hour window (the date is otherwise nowhere on screen) and for a 30-day
 *  one (the time is).
 */
export function xTooltipLabel(spec: ChartSpec, value: number): string {
  if (spec.xTickFormat) return spec.xTickFormat(value)
  if (spec.categorical) return spec.labels[Math.round(value)] ?? ''
  if (!spec.timed) return spec.labels.length ? spec.labels[Math.round(value)] ?? '' : String(value)
  const at = new Date(value * 1000)
  const z = zoned(spec.tz)
  if (spec.xSpan > 330 * DAY) {
    return at.toLocaleDateString(undefined, z({ month: 'short', day: 'numeric', year: 'numeric' }))
  }
  const date = at.toLocaleDateString(undefined, z({ month: 'short', day: 'numeric' }))
  const time = at.toLocaleTimeString(undefined, z({ hour: '2-digit', minute: '2-digit' }))
  return `${date}, ${time}`
}

/** Round a plotted value the way both the axis and the tooltip should show it. */
export function formatValue(value: number, unit: string): string {
  return `${Math.round(value * 10) / 10}${unit}`
}

/** Pick whichever of two theme tokens reads better on `background`.
 *  Both inputs are resolved tokens, so no hex ever escapes into a component. */
export function pickReadable(background: string, dark: string, light: string): string {
  return relativeLuminance(background) > 0.5 ? dark : light
}

function relativeLuminance(color: string): number {
  const hex = color.trim().match(/^#([0-9a-f]{6})$/i)?.[1]
  let r = 0
  let g = 0
  let b = 0
  if (hex) {
    const n = parseInt(hex, 16)
    r = ((n >> 16) & 255) / 255
    g = ((n >> 8) & 255) / 255
    b = (n & 255) / 255
  } else {
    const parts = color.match(/^rgba?\(([^)]+)\)/)?.[1]?.split(',')
    if (!parts) return 0
    r = Number(parts[0]) / 255
    g = Number(parts[1]) / 255
    b = Number(parts[2]) / 255
  }
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

/** Screen-reader summary of a chart — canvas has no readable content of its own. */
export function describeChart(spec: ChartSpec): string {
  const shape = spec.type === 'doughnut' ? 'doughnut chart' : `${spec.type} chart`
  const parts = spec.series.map((s) => {
    const values = spec.categorical
      ? s.data.filter((v): v is number => v !== null)
      : s.points.map((p) => p.y)
    if (!values.length) return `${s.label}: no data`
    const min = Math.min(...values)
    const max = Math.max(...values)
    const avg = values.reduce((a, b) => a + b, 0) / values.length
    return `${s.label}: min ${formatValue(min, s.unit)}, average ${formatValue(avg, s.unit)}, max ${formatValue(max, s.unit)}`
  })
  return `${shape}${spec.title ? `: ${spec.title}` : ''}. ${parts.join('. ')}`
}
